import { useNavigate } from "react-router-dom";
import {
  Globe, CalendarCheck, CreditCard, ShoppingBag, Users, LayoutDashboard,
  ArrowUpRight, MessageCircle, Check, ArrowRight, Smartphone, Server, MonitorSmartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/* WhatsApp number is configurable via REACT_APP_WHATSAPP_NUMBER (digits only, incl. country code).
   To change it later, edit REACT_APP_WHATSAPP_NUMBER in /app/frontend/.env */
const WA_NUMBER = (process.env.REACT_APP_WHATSAPP_NUMBER || "447405068928").replace(/\D/g, "");
const WA_MESSAGE = "Hi, I'm interested in a website for my business. Could we discuss my requirements and pricing?";
const WA_HREF = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_MESSAGE)}`;

const services = [
  { icon: Globe, title: "Business websites", desc: "Clean, professional sites that represent your brand and work beautifully on every device." },
  { icon: CalendarCheck, title: "Booking systems", desc: "Let customers check availability and book appointments directly from your website." },
  { icon: CreditCard, title: "Payment integrations", desc: "Take deposits or full payments securely with Stripe, Square and other providers." },
  { icon: ShoppingBag, title: "Online shops", desc: "Sell products or services online with a simple, reliable e-commerce setup." },
  { icon: Users, title: "Customer portals", desc: "Give your customers a secure login to manage their account, orders or bookings." },
  { icon: LayoutDashboard, title: "Custom dashboards", desc: "Bespoke tools and dashboards built around how your business actually runs." },
];

const projects = [
  { img: "/feat-freddies.jpg", title: "Freddie's Afro Caribbean Store", tag: "Online grocery shop", url: "https://freddiesafrocaribbean.co.uk/", domain: "freddiesafrocaribbean.co.uk" },
  { img: "/feat-snbk.jpg", title: "SNBK Ltd — StyleNation", tag: "Salon bookings & shop", url: "https://snbkltd.co.uk/", domain: "snbkltd.co.uk" },
  { img: "/feat-kimar.jpg", title: "Kimar", tag: "Fashion e-commerce", url: "https://kimar.org/", domain: "kimar.org" },
];

const WAButton = ({ className = "", label = "Discuss Your Website on WhatsApp", testid }) => (
  <a href={WA_HREF} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className={`inline-flex items-center justify-center gap-2.5 rounded-md bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold px-6 h-12 text-base transition-colors shadow-sm ${className}`}>
    <MessageCircle className="h-5 w-5" /> {label}
  </a>
);

export default function OperaMindWeb() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="web-brand-logo" className="flex items-center gap-1.5">
            <img src="/operamind-mark.png" alt="OperaMind" className="h-10 w-10 object-contain" />
            <div className="flex flex-col leading-none text-left">
              <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">Opera<span className="text-blue-600">Mind</span> <span className="text-slate-400 font-bold">Web</span></span>
              <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-900">Websites. Built for business.</span>
            </div>
          </button>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#services" className="hover:text-slate-900 transition-colors">Services</a>
            <a href="#work" className="hover:text-slate-900 transition-colors">Our Work</a>
            <a href="#pricing" className="hover:text-slate-900 transition-colors">Pricing</a>
            <a href="#consultation" className="hover:text-slate-900 transition-colors">Consultation</a>
            <button onClick={() => navigate("/")} className="hover:text-slate-900 transition-colors">AI Receptionist</button>
          </nav>
          <WAButton testid="nav-whatsapp-btn" label="Message us" className="h-10 px-4 text-sm" />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="glow-blob glow-blue glow-float h-80 w-80 -top-10 -left-10 opacity-40" />
        <div className="glow-blob glow-sky glow-float h-72 w-72 top-32 right-0 opacity-30" style={{ animationDelay: "3s" }} />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
              <Globe className="h-3.5 w-3.5" /> OperaMind Web
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.03] text-slate-900">
              Websites built<br />around your <span className="text-blue-600">business.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-lg">
              We create professional, mobile-friendly websites tailored to your business, with options
              for online bookings, payments, e-commerce and custom features.
            </p>
            <div className="mt-7">
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Websites starting from</p>
              <p className="font-heading text-5xl font-extrabold tracking-tight text-blue-600">£400</p>
              <p className="mt-1 text-sm text-slate-500">Final pricing depends on your project requirements.</p>
            </div>
            <div className="mt-8">
              <WAButton testid="hero-whatsapp-btn" />
              <p className="mt-3 text-xs text-slate-400">No obligation — just a friendly chat about what you need.</p>
            </div>
          </div>
          <div className="relative reveal">
            <div className="absolute -inset-4 bg-gradient-to-tr from-blue-100 to-transparent rounded-3xl blur-2xl opacity-70" />
            <img
              src="/operamind-web-hero.jpg"
              alt="Professional business website shown on a laptop"
              data-testid="hero-mockup-image"
              className="relative w-full rounded-2xl border border-slate-200 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)]"
            />
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-16">
        <div className="max-w-2xl reveal">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">What we build</p>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Everything your business needs online.
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Pick the features that fit your business — we'll build a site around them.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={s.title} data-testid={`service-${i}`} data-delay={(i % 3) + 1} className="reveal card-fancy group p-6">
              <div className="h-11 w-11 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <s.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-heading font-bold text-lg tracking-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        {/* Feature strip */}
        <div className="mt-8 reveal rounded-2xl border border-slate-200 bg-white px-6 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-medium text-slate-600">
          {[
            { icon: Server, label: "Domain & hosting setup" },
            { icon: MonitorSmartphone, label: "Responsive design" },
            { icon: Smartphone, label: "Mobile-first" },
            { icon: Check, label: "Ongoing support" },
          ].map((f) => (
            <span key={f.label} className="inline-flex items-center gap-2"><f.icon className="h-4 w-4 text-blue-600" /> {f.label}</span>
          ))}
        </div>
      </section>

      {/* Our Work */}
      <section id="work" className="max-w-7xl mx-auto px-6 py-16">
        <div className="max-w-2xl reveal">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Recent work</p>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            A few sites we've built.
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Clean, mobile-friendly designs tailored to each business — from bookings to online shops.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((p, i) => (
            <a key={p.url} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`project-${i}`} data-delay={(i % 3) + 1}
              className="reveal group block rounded-xl border border-slate-200 bg-white overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-16px_rgba(15,23,42,0.3)] hover:border-blue-200">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
                <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
                <span className="ml-2 flex-1 truncate rounded bg-white border border-slate-200 px-2 py-0.5 text-[11px] text-slate-500">{p.domain}</span>
              </div>
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                <img src={p.img} alt={`${p.title} website preview`} loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]" />
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-slate-900/80 text-white text-xs font-semibold px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  Visit live site <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="font-heading font-bold text-sm tracking-tight text-slate-900 truncate">{p.title}</span>
                <span className="shrink-0 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold px-2.5 py-0.5">{p.tag}</span>
              </div>
            </a>
          ))}
        </div>
        <div className="mt-8 text-center reveal">
          <WAButton testid="work-whatsapp-btn" label="Discuss your website on WhatsApp" />
        </div>
      </section>

      {/* Pricing band */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-12">
        <div className="reveal relative overflow-hidden rounded-2xl bg-slate-900 text-white p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center">
          <div className="glow-blob glow-indigo glow-float h-72 w-72 -top-16 -right-10 opacity-30" />
          <div className="relative">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400">Simple pricing</p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight">Websites starting from £400.</h2>
            <p className="mt-3 text-slate-300 leading-relaxed max-w-md">
              No complicated tiers. Tell us what you need and we'll put together a clear, personalised
              quote. Final pricing depends on your project requirements.
            </p>
          </div>
          <div className="relative lg:justify-self-end">
            <WAButton testid="pricing-whatsapp-btn" label="Get a personalised quote on WhatsApp" />
          </div>
        </div>
      </section>

      {/* Consultation */}
      <section id="consultation" className="max-w-7xl mx-auto px-6 py-16">
        <div className="reveal max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
            <MessageCircle className="h-3.5 w-3.5" /> Let's talk
          </span>
          <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Discuss your website, get a personalised quote.
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Message us on WhatsApp to talk through what your business needs. We'll discuss your
            requirements, answer your questions and send you a clear, personalised quote — no pressure,
            no obligation.
          </p>
          <div className="mt-8 flex justify-center">
            <WAButton testid="consultation-whatsapp-btn" />
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-slate-500">
            {["Quick, friendly reply", "Clear personalised quote", "No obligation"].map((t) => (
              <span key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5">
            <img src="/operamind-mark.png" alt="OperaMind" className="h-7 w-7 object-contain" />
            <span className="font-heading font-bold text-slate-900">Opera<span className="text-blue-600">Mind</span> <span className="text-slate-400">Web</span></span>
          </button>
          <p>© {new Date().getFullYear()} OperaMind — Websites built around your business.</p>
          <div className="flex items-center gap-5">
            <button onClick={() => navigate("/")} className="hover:text-slate-900 transition-colors">AI Receptionist</button>
            <a href="/privacy" className="hover:text-slate-900 transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-slate-900 transition-colors">Terms</a>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp button */}
      <a
        href={WA_HREF}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-btn"
        aria-label="Discuss your website on WhatsApp"
        className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2.5 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold pl-4 pr-5 py-3.5 shadow-lg shadow-emerald-600/25 transition-transform hover:scale-105"
      >
        <MessageCircle className="h-5 w-5" />
        <span className="hidden sm:inline text-sm">Chat on WhatsApp</span>
      </a>
    </div>
  );
}
